import { PropsWithChildren } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate } from 'react-router-dom';

import {
  getHostingSitesV2QueryKey,
  useAccountHostingList,
  useDbUsage,
} from '@newfold/huapi-js';
import { HostingSitesV2Params } from '@newfold/huapi-js/src/index.schemas';

import { useAlert } from '~/components/Alerts/alertsStore';
import useAccount from '~/hooks/useAccount';
import useAMRevamp from '~/hooks/useAMRevamp';
import useFeature from '~/hooks/useFeature';
import useFilteredHostingSites from '~/hooks/useFilteredHostingSites';
import useHostingInfo from '~/hooks/useHostingInfo';
import AddWebsiteMenu from '~/Nova/scenes/Site/scenes/AddWebsite/components/AddWebsiteMenu';
import { SiteOption } from '~/Nova/scenes/Site/scenes/AddWebsite/hooks/useSiteOptions';
import ResourcesWarningModal from '~/scenes/Hosting/scenes/HostingDetail/scenes/Overview/Cloud/components/ResourcesWarningModal';

export interface ResourceLimitType {
  limitReached: boolean;
  is5sHostingType?: boolean;
}
export let customSitesQueryKey: (string | HostingSitesV2Params)[] = [];
const AddSiteValidation = ({
  open = false,
  showingAllSites = false,
  setSelected,
  setIsDBUsageLoading,
  selectedHostingId = undefined,
  availableSitesToAdd = undefined,
  menuAnchorEl = undefined,
  setMenuAnchorEl = (el: HTMLElement | null) => {},
  onMenuOptionClick = () => {},
}: PropsWithChildren<{
  open?: boolean;
  setSelected: Function;
  setIsDBUsageLoading: Function;
  showingAllSites?: boolean;
  selectedHostingId?: string | null;
  availableSitesToAdd?: number | null;
  menuAnchorEl?: HTMLElement | null;
  setMenuAnchorEl?: (el: HTMLElement | null) => void;
  onMenuOptionClick?: (option: SiteOption) => void;
}>) => {
  const { t } = useTranslation('sites');
  const [isAtomic] = useFeature('atomic');
  const { isAMRevamp } = useAMRevamp();
  const resourceLimitMap: { [key: string]: ResourceLimitType } = {};

  const generateAlert = useAlert();
  const { id } = useAccount();
  const hostingId = selectedHostingId || id;
  customSitesQueryKey = [
    ...getHostingSitesV2QueryKey(hostingId),
    '/hostingSites',
  ];
  const { data: hostingSites } = useFilteredHostingSites(
    hostingId,
    {},
    {
      query: {
        enabled: !!hostingId && !isAMRevamp,
        queryKey: customSitesQueryKey,
      },
    },
  );

  const availableSites = isAMRevamp
    ? availableSitesToAdd
    : hostingSites?.data?.available_sites;

  const { data: hostingList, isError } = useAccountHostingList(undefined, {
    query: {
      enabled: showingAllSites,
    },
  });
  const hostingIdInfoParam = isAMRevamp ? { hostingId } : undefined;
  const { hostingAccount, isDisabled } = useHostingInfo(hostingIdInfoParam);

  const cloudHosting = hostingAccount?.data?.type === 'cluster_atomic';
  const workers = hostingAccount?.data?.resources?.workers;
  const storage = hostingAccount?.data?.resources?.storage;
  const isHosting5s = hostingAccount?.data?.plan_key === 'hosting_05s';

  const isRestrictDbUsageCall = isDisabled ? false : !!hostingId;
  const { data: dbUsage, isPending } = useDbUsage(hostingId, {
    query: {
      enabled: isRestrictDbUsageCall && open && !isAtomic,
    },
  });

  const db_limit = dbUsage?.data?.db_limit;
  const db_count = dbUsage?.data?.db_count;

  // handle close validation
  const handleCloseValidation = () => {
    return setSelected(false);
  };

  if (!open) return null;

  // I. Multi Hosting checks
  if (showingAllSites) {
    //@ts-ignore
    const { accounts } = hostingList?.data;
    // A. If there is an error or no accounts, show error alert
    if (isError || !accounts) {
      generateAlert({
        severity: 'error',
        description: t('addSiteErrMsg'),
        showCloseBtn: true,
        persist: false,
      });
      setSelected(false);
      return null;
    }

    // B. If there is only one account, go to add site flow
    if (accounts?.length === 1) {
      const { current_sites, max_sites } = accounts[0];

      const maxSitesValue =
        max_sites === 'unlimited' ? Infinity : parseInt(max_sites) || null;

      // i. If there are no available sites,
      const availableSites = maxSitesValue
        ? Math.max(maxSitesValue - current_sites, 0)
        : null;

      // i. If there are no available sites, show error alert
      resourceLimitMap.sites = { limitReached: !availableSites };

      // ii. Cloud Hosting specific check - PHP workers limit
      const noAvailableWorkers =
        typeof workers?.available === 'number' && workers?.available < 2;

      resourceLimitMap.phpWorker = {
        limitReached: cloudHosting && noAvailableWorkers,
      };

      // iii. Cloud Hosting specific check - Data Storage limit
      const noAvailableStorage =
        typeof storage?.available === 'number' && storage?.available < 1;

      resourceLimitMap.storage = {
        limitReached: cloudHosting && noAvailableStorage,
      };

      // iv. Check Database limit
      const noAvailableDB = db_limit && db_count && db_count >= db_limit;

      resourceLimitMap.db = {
        limitReached: !isAtomic && (noAvailableDB as boolean),
      };
      if (
        !isPending &&
        Object.keys(resourceLimitMap).filter(
          (resource) => resourceLimitMap[resource].limitReached,
        ).length > 0
      ) {
        setIsDBUsageLoading(false);
        return (
          <ResourcesWarningModal
            resourceLimitMap={resourceLimitMap}
            open
            setOpen={handleCloseValidation}
          />
        );
      }
      // iv. If there are available sites, go to add site flow
      return <Navigate to="./add" />;
    }

    // C. If there are multiple accounts, go to hosting list so the user can select an account
    if (accounts?.length > 1) {
      window.location.href = '/my-account/hosting/list';
      return null;
    }
  }

  // II. Check Sites limit
  // if availableSites = null, then it means unlimited sites
  const noAvailableSites =
    typeof availableSites === 'number' && availableSites <= 0;

  resourceLimitMap.sites = {
    limitReached: noAvailableSites,
    is5sHostingType: isHosting5s,
  };

  // III. Check Database limit
  const noAvailableDB = db_limit && db_count && db_count >= db_limit;

  resourceLimitMap.db = {
    limitReached: !isAtomic && (noAvailableDB as boolean),
  };

  // IV. Cloud Hosting specific check - PHP workers limit
  const noAvailableWorkers =
    typeof workers?.available === 'number' && workers?.available < 2;

  resourceLimitMap.phpWorker = {
    limitReached: cloudHosting && noAvailableWorkers,
  };

  // V. Cloud Hosting specific check - Data Storage limit
  const noAvailableStorage =
    typeof storage?.available === 'number' && storage?.available < 1;

  resourceLimitMap.storage = {
    limitReached: cloudHosting && noAvailableStorage,
  };

  const handlingWarningModelLoadingState = isAtomic || !isPending;
  if (
    handlingWarningModelLoadingState &&
    Object.keys(resourceLimitMap).filter(
      (resource) => resourceLimitMap[resource].limitReached,
    ).length > 0
  ) {
    setIsDBUsageLoading(false);
    return (
      <ResourcesWarningModal
        resourceLimitMap={resourceLimitMap}
        open
        setOpen={handleCloseValidation}
      />
    );
  }
  // VI. All checks pass, go to add site flow
  if (isPending && !isAtomic) {
    setIsDBUsageLoading(true);
    return null;
  }

  if (isAMRevamp) {
    setIsDBUsageLoading(false);
    return (
      <AddWebsiteMenu
        isCloudHosting={cloudHosting}
        menuAnchorEl={menuAnchorEl}
        setMenuAnchorEl={setMenuAnchorEl}
        onMenuOptionClick={onMenuOptionClick}
      />
    );
  }

  return <Navigate to="sites/add" />;
};

export default AddSiteValidation;
